import React, { useState, useEffect, useRef } from "react";
import {
  Menu,
  Search,
  Bell,
  User as UserIcon,
  LogOut,
  Settings,
  Sun,
  Moon,
  Check,
} from "lucide-react";
import useAppLevelAuth from "../hooks/useAppLevelAuth";
import { useHistory } from "react-router-dom";
// import { createPageUrl } from "@/utils";
// import { User } from "@/entities/User";

// MUI
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Avatar from "@mui/material/Avatar";
import Popover from "@mui/material/Popover";
import Button from "@mui/material/Button";
import Badge from "@mui/material/Badge";
import MenuMui from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { Link } from "react-router-dom";

// Additional imports for Header.js elements
import jwtDecode from "jwt-decode";
import { errorAlert, handleSuccess } from "../Components/Alerts";
import authHeader from "../Auth/authHeader";
import { ApiLink } from "../APILink/ApiLink";
import axios from "axios";
import { format } from "date-fns";
import LicenseBanner from "../Components/LicenseBanner";

import "./NewHeader.scss";

export default function NewHeader({
  onToggleSidebar,
  searchTerm = "",
  onSearchChange,
  themeMode = "light",
  onSetTheme,
  showThemeSwitcher = true,
}) {
  const { logout } = useAppLevelAuth();
  const history = useHistory();

  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Ref for profile wrapper to detect outside clicks
  const profileRef = useRef(null);
  const searchInputRef = useRef(null);

  // Additional state from Header.js
  const [email, setEmail] = useState("");
  const headers = authHeader();
  const [userNotification, setUserNotification] = useState([]);
  const [logo, setLogo] = useState("");
  const [companyName, setCompanyName] = useState("");

  // Forms menu state
  const [forms, setForms] = useState(null);
  const openForms = Boolean(forms);
  const [themeMenuAnchor, setThemeMenuAnchor] = useState(null);

  // Notifications popover state
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [notifList, setNotifList] = useState(userNotification);

  const getLogoSrc = (logoValue) => {
    const fallback = ApiLink.PHOTO_URL + "companyLogo.png";
    if (!logoValue) return fallback;

    const normalized = String(logoValue).trim();
    if (!normalized) return fallback;

    if (/^https?:\/\//i.test(normalized)) {
      return normalized;
    }

    return ApiLink.PHOTO_URL + normalized;
  };

  useEffect(() => {
    // User.me().then(setCurrentUser).catch(() => {});
    // Mock user for now
    setCurrentUser({ firstName: "Përdorues", lastName: "", role: "Administrator", email: "user@example.com" });

    // From Header.js
    const token = localStorage.getItem("token");
    if (token) {
      const decodedToken = jwtDecode(token);
      setEmail(decodedToken.email);
      // setRole(decodedToken.role);
    }

    fetchCompanyInfo();
    fetchUserNotification();
  }, []);

  // Handle click outside to close profile dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };

    if (isProfileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileOpen]);

  const handleLogout = async () => {
    await logout();
    history.push("/");
  };

  // Functions from Header.js
  const fetchCompanyInfo = async () => {
    try {
      const response = await axios.get(ApiLink.API_URL + "Companies/getLogo", {
        headers,
      });

      const companyInfo = Array.isArray(response?.data)
        ? response.data[0]
        : response?.data;

      setLogo(companyInfo?.CompanyLogo || "");
      setCompanyName(companyInfo?.CompanyName || "");
    } catch (error) {
      errorAlert("Gabim në kërkesë:", error);
      if (
        error.response &&
        error.response.data &&
        error.response.data.errorMessage
      ) {
        errorAlert(
          `Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`
        );
      } else if (error.message) {
        errorAlert(`Gabim: ${error.message}`);
      } else {
        errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
      }
    }
  };

  const fetchUserNotification = async () => {
    try {
      const response = await axios.get(
        ApiLink.API_URL + "Notification/notificationByID",
        { headers }
      );
      setUserNotification(response.data);
      setNotifList(response.data);
    } catch (error) {
      errorAlert("Gabim në kërkesë:", error);
      if (
        error.response &&
        error.response.data &&
        error.response.data.errorMessage
      ) {
        errorAlert(
          `Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`
        );
      } else if (error.message) {
        errorAlert(`Gabim: ${error.message}`);
      } else {
        errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
      }
    }
  };

  const updateStatusNtfc = async (id) => {
    try {
      const response = await axios.put(
        ApiLink.API_URL + `Notification/statusNotification/${id}`,
        null,
        { headers }
      );
    } catch (e) {
      // errorAlert("Nuk është modifikuar", e.response);
    }
  };

  const navigateToList = async (id) => {
    closeNotifPopover();
    await updateStatusNtfc(id);
    await fetchUserNotification();
    history.push("/configuration/notification");
  };

  const setNotificationAsRead = (id) => {
    setNotifList((prev) =>
      prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
    );
  };

  // Handlers for forms menu
  const handleClickForms = (event) => {
    setForms(event.currentTarget);
  };
  const handleCloseForms = () => {
    setForms(null);
  };

  // Handlers for notifications
  const handleNotifClick = (event) => {
    setNotifAnchor(event.currentTarget);
  };
  const closeNotifPopover = () => {
    setNotifAnchor(null);
  };
  const notifPopoverOpen = Boolean(notifAnchor);
  const openThemeMenu = Boolean(themeMenuAnchor);

  const themeOptions = [
    {
      value: "light",
      label: "Light",
      description: "Tema klasike e bardhë",
      icon: Sun,
    },
    {
      value: "dark-green",
      label: "Dark Green",
      description: "Dark me nuancat aktuale green",
      icon: Moon,
    },
    {
      value: "dark-black",
      label: "Black Dark",
      description: "Sfond i zi me cards dark green",
      icon: Moon,
    },
  ];

  const isDarkTheme = themeMode !== "light";

  const handleSearchIconClick = () => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
      searchInputRef.current.select();
    }
  };

  const hasSearchValue = searchTerm.trim().length > 0;

  const handleClearSearch = () => {
    if (onSearchChange) {
      onSearchChange("");
    }
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleOpenThemeMenu = (event) => {
    setThemeMenuAnchor(event.currentTarget);
  };

  const handleCloseThemeMenu = () => {
    setThemeMenuAnchor(null);
  };

  const handleSelectTheme = (nextTheme) => {
    if (onSetTheme) {
      onSetTheme(nextTheme);
    }
    handleCloseThemeMenu();
  };

  return (
    <header className="header">
      <LicenseBanner />
      <div className="header-left">
        <IconButton
          onClick={onToggleSidebar}
          className="icon-btn menu-toggle-btn"
        >
          <Menu className="icon-20" />
        </IconButton>

        <div className={`search-box ${hasSearchValue ? "has-value" : ""}`}>
          <button
            type="button"
            className="search-trigger"
            onClick={handleSearchIconClick}
            aria-label="Hap kerkimin"
          >
            <Search className="search-icon icon-16" />
          </button>
          <input
            ref={searchInputRef}
            type="text"
            id="sidebar-menu-search"
            name="sidebar-menu-search"
            autoComplete="off"
            placeholder="Kerko menu/nënmenu..."
            aria-label="Kerko menu"
            value={searchTerm}
            onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
          />
          {hasSearchValue && (
            <button
              type="button"
              className="search-clear-btn"
              onClick={handleClearSearch}
              aria-label="Pastro kerkimin"
            >
              <span className="search-clear-glyph">X</span>
            </button>
          )}
        </div>
      </div>

      <div className="header-right">
        {showThemeSwitcher && (
          <>
            <div className="header-action-slot">
              <IconButton
                onClick={handleOpenThemeMenu}
                className="icon-btn header-action-btn theme-toggle-btn"
                aria-label="Ndrysho temën"
                title="Ndrysho temën"
              >
                {themeMode === "light" ? (
                  <Sun className="icon-20" />
                ) : themeMode === "dark-black" ? (
                  <Moon className="icon-20" />
                ) : (
                  <Moon className="icon-20" />
                )}
              </IconButton>
            </div>

            <MenuMui
              anchorEl={themeMenuAnchor}
              id="theme-menu"
              open={openThemeMenu}
              onClose={handleCloseThemeMenu}
              PaperProps={{
                elevation: 0,
                className: "theme-menu-paper",
                sx: {
                  mt: 1,
                  borderRadius: "14px",
                  overflow: "hidden",
                  border: isDarkTheme
                    ? "1px solid rgba(111,150,132,0.34)"
                    : "1px solid rgba(40,73,69,0.14)",
                  boxShadow: isDarkTheme
                    ? "0 10px 28px rgba(0,0,0,0.34)"
                    : "0 8px 24px rgba(15,23,42,0.14)",
                  backgroundColor: isDarkTheme ? "#1a3531" : "#ffffff",
                },
              }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              <div className="theme-menu-shell">
                <div className="theme-menu-head">
                  <p className="theme-menu-head__title">Zgjedh Temën</p>
                  <p className="theme-menu-head__subtitle">Personalizo pamjen e sistemit</p>
                </div>

                <div className="theme-menu-list">
                  {themeOptions.map((option) => {
                    const selected = themeMode === option.value;
                    const OptionIcon = option.icon;

                    return (
                      <MenuItem
                        key={option.value}
                        selected={selected}
                        onClick={() => handleSelectTheme(option.value)}
                        className={`theme-menu-item ${selected ? "is-active" : ""}`}
                      >
                        <span className={`theme-menu-dot theme-menu-dot--${option.value}`}></span>
                        <span className="theme-menu-item__icon-wrap">
                          <OptionIcon className="theme-menu-item__icon" />
                        </span>

                        <span className="theme-menu-item__copy">
                          <span className="theme-menu-item__label">{option.label}</span>
                          <span className="theme-menu-item__description">{option.description}</span>
                        </span>

                        {selected && (
                          <span className="theme-menu-item__status" aria-label="Tema aktive">
                            <Check className="theme-menu-item__check" />
                            Aktive
                          </span>
                        )}
                      </MenuItem>
                    );
                  })}
                </div>
              </div>
            </MenuMui>
          </>
        )}

        <div className="header-action-slot">
          <IconButton onClick={handleClickForms} className="icon-btn header-action-btn">
            <i className="bi bi-grid-3x3-gap-fill"></i>
          </IconButton>
        </div>

        <MenuMui
          anchorEl={forms}
          id="forms-menu"
          open={openForms}
          onClose={handleCloseForms}
          PaperProps={{
            elevation: 0,
            sx: {
              mt: 1,
              borderRadius: "16px",
              overflow: "hidden",
              border: "1px solid rgba(40,73,69,0.10)",
              boxShadow: "0 8px 30px rgba(15,23,42,0.14)",
            },
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <div className="app-launcher">
            <div className="app-launcher__header">Hyrje e Shpejtë</div>
            <div className="app-launcher__grid">
              {[
                { to: "/Sales/Addsalesindetails", icon: "bi-cart-check-fill", label: "Shitje" },
                { to: "/purchases/addpurchaseinvoice", icon: "bi-bag-check-fill", label: "Blerje" },
                { to: "/expences/expensepurchases", icon: "bi-receipt-cutoff", label: "Shpenzime" },
                { to: "/articles/articleslist", icon: "bi-upc-scan", label: "Artikuj" },
                { to: "/accounts/clients", icon: "bi-person-plus-fill", label: "Klientë" },
                { to: "/accounts/suppliers", icon: "bi-person-dash-fill", label: "Furnizues" },
                { to: "/money/addreceivables", icon: "bi-cash-coin", label: "Arkëtime" },
                { to: "/money/addpayments", icon: "bi-wallet2", label: "Pagesa" },
                { to: "/money/addtransfers", icon: "bi-arrow-left-right", label: "Transferta" },
              ].map(({ to, icon, label }) => (
                <Link key={to} to={to} className="app-launcher__tile" onClick={handleCloseForms}>
                  <div className="app-launcher__tile-icon">
                    <i className={`bi ${icon}`}></i>
                  </div>
                  <span className="app-launcher__tile-label">{label}</span>
                </Link>
              ))}
            </div>
          </div>
        </MenuMui>

        <div className="header-action-slot">
          <IconButton
            onClick={handleNotifClick}
            className="icon-btn header-action-btn notification-btn"
          >
            <Badge badgeContent={userNotification.length || null} color="error">
              <Bell className="icon-20" />
            </Badge>
            <span className="notification-dot"></span>
          </IconButton>
        </div>

        <Popover
          open={notifPopoverOpen}
          anchorEl={notifAnchor}
          onClose={closeNotifPopover}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{ style: { width: 350 } }}
        >
          <Box p={2} borderBottom="1px solid #e0e0e0">
            <Typography variant="subtitle1">Njoftime</Typography>
            <Typography variant="caption" color="text.secondary">
              {userNotification.length} Të palexuara
            </Typography>
          </Box>

          <List sx={{ maxHeight: 300, overflowY: "auto" }}>
            {userNotification.length === 0 ? (
              <Box p={1}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                >
                  Nuk ka asnjë njoftim
                </Typography>
              </Box>
            ) : (
              notifList.map((notification) => (
                <div
                  key={notification.NotificationStatusID}
                  onClick={() =>
                    navigateToList(notification.NotificationStatusID)
                  }
                  style={{
                    bgcolor: notification.IsRead ? "inherit" : "#e3f2fd",
                    alignItems: "flex-start",
                    textDecoration: "none",
                    color: "inherit",
                    cursor: "pointer",
                  }}
                >
                  <ListItem
                    button
                    onClick={() =>
                      setNotificationAsRead(notification.NotificationStatusID)
                    }
                    sx={{
                      bgcolor: notification.isRead ? "inherit" : "#e3f2fd",
                      alignItems: "flex-start",
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: "#1976d2", fontSize: 12 }}>
                        {notification.Version || "!"}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography
                          variant="subtitle2"
                          fontWeight={notification.IsRead ? "normal" : "bold"}
                        >
                          {notification.Title}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              cursor: "pointer",
                            }}
                          >
                            {notification.Message}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            {format(
                              new Date(notification.CreatedDate),
                              "MMM d, h:mm a"
                            )}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                </div>
              ))
            )}
          </List>

          <Box p={2} textAlign="center">
            <Link to={"/configuration/notification"}>
              <Button
                size="small"
                onClick={closeNotifPopover}
              >
                Shiko te gjitha njoftimet
              </Button>
            </Link>
          </Box>
        </Popover>

        <div className="profile-wrapper header-action-slot" ref={profileRef}>
          <button
            onClick={() => setIsProfileOpen(!isProfileOpen)}
            className="profile-btn"
          >
            <div className="profile-text">
              <p className="name">
                {companyName || "Kompania"}
              </p>
              <p className="role">
                {email ? email.split("@acctree.net")[0] : "Përdoruesi"}
              </p>
            </div>

            <div className="avatar">
              <img
                src={getLogoSrc(logo)}
                alt="Logo e kompanisë"
                loading="lazy"
                style={{
                  width: "32px",
                  height: "32px",
                  objectFit: "cover",
                  display: "block",
                  borderRadius: "50%",
                  border: "solid #284945 1px",
                }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = ApiLink.PHOTO_URL + "companyLogo.png";
                }}
              />
            </div>
          </button>

          {isProfileOpen && (
            <div className="dropdown">
              <div className="dropdown-header">
                <p className="signed">I kyçur si</p>
                <p className="email">
                  {email || "user@example.com"}
                </p>
              </div>

              <div className="dropdown-section">
                <Link
                  to="/configuration/profile"
                  style={{ textDecoration: "none", color: "black" }}
                >
                  <button className="dropdown-item">
                    <UserIcon className="icon-16" /> Profili
                  </button>
                </Link>
                <Link
                  to="/configuration/users"
                  style={{ textDecoration: "none", color: "black" }}
                >
                  <button className="dropdown-item">
                    <Settings className="icon-16" /> Përdoruesit
                  </button>
                </Link>
              </div>

              <div className="dropdown-section border-top">
                <button
                  onClick={handleLogout}
                  className="dropdown-item logout"
                >
                  <LogOut className="icon-16" /> Dil
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}